import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, errorText } from "@/lib/adminApi";
import { countEnquiries, filterEnquiries } from "./inboxSelectors";

export const useEnquiryInbox = () => {
  const nav = useNavigate();
  const request = useRef(null);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setError("");
    try {
      const { data } = await adminApi.get("/admin/enquiries", { signal: controller.signal });
      if (!controller.signal.aborted) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (controller.signal.aborted) return;
      if ([401, 403].includes(err.response?.status)) nav("/admin/login", { replace: true });
      else setError("Enquiries could not be loaded. Please refresh to try again.");
    }
  }, [nav]);

  useEffect(() => {
    load();
    return () => request.current?.abort();
  }, [load]);

  const shown = useMemo(() => filterEnquiries(items ?? [], filter, q), [items, filter, q]);
  const counts = useMemo(() => countEnquiries(items ?? []), [items]);
  const current = (items ?? []).find((item) => item.id === selected);
  const reportError = (err) => toast.error(errorText(err.response?.data?.detail));

  const setStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/admin/enquiries/${id}`, { status });
      setItems((list) => (list ?? []).map((item) => item.id === id ? data : item));
      toast.success(`Marked as ${status}`);
    } catch (err) { reportError(err); }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/enquiries/${id}`);
      setItems((list) => (list ?? []).filter((item) => item.id !== id));
      setSelected((value) => value === id ? null : value);
      toast.success("Enquiry deleted");
    } catch (err) { reportError(err); }
  };

  const logout = async () => {
    try {
      await adminApi.post("/auth/logout");
      nav("/admin/login", { replace: true });
    } catch (err) { reportError(err); }
  };
  return { items, shown, counts, current, filter, q, selected, error, setFilter, setQ, setSelected, load, setStatus, remove, logout };
};